import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getSpecificWorkspace } from '../actions/workspaceAction';
import { PermissionProvider } from '../pages/authentication/permission-providor/context/PermissionProvider';
import LoadingOverlay from '../components/LoadingOverlay';

const WorkspaceRoute = ({ children }) => {
  const dispatch = useDispatch();

  // The URL is the authority for which workspace is open — not localStorage.
  // Keying off `workspaceID` meant a deep link (or a shared/bookmarked URL) to
  // workspace B loaded workspace A's details, so permissions, members and
  // locales came from the wrong workspace while the page showed B's content.
  // And when localStorage had no id at all — a new device, a cleared profile —
  // nothing was fetched, `workspaceInfo` stayed null, and PermissionProvider
  // fell back to 'viewer', disabling every editing control for everyone.
  const { workspaceId: routeWorkspaceId } = useParams();
  const workspaceID = routeWorkspaceId || localStorage.getItem('workspaceID');

  const workspaceInfo = useSelector((state) => state.workspace.workspaceInfo);
  const loadedWorkspaceId = workspaceInfo?.data?._id;
  const needsFetch = !!workspaceID && String(loadedWorkspaceId) !== String(workspaceID);

  const [loading, setLoading] = useState(needsFetch);

  useEffect(() => {
    let cancelled = false;

    const fetchWorkspace = async () => {
      if (!needsFetch) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        await dispatch(getSpecificWorkspace(workspaceID));
        // Keep the stored id in step for the rest of the app, which still
        // reads it — otherwise moving between workspaces by URL leaves it
        // pointing at whichever one was opened from the list last.
        localStorage.setItem('workspaceID', workspaceID);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchWorkspace();
    return () => { cancelled = true; };
  }, [workspaceID, needsFetch, dispatch]);

  if (loading) return <LoadingOverlay />;

  return (
    <PermissionProvider>
      {children}
    </PermissionProvider>
  );
};

export default WorkspaceRoute;
